import type { GpuContext } from '../context.js';
import { workgroupCount } from '../context.js';
import type { GpuImage } from '../resources.js';
import { Struct } from '../uniform-writer.js';
import { FILTER_AREA } from '../types.js';
import { morphology } from './morphology.js';
import { resampleTo } from './resample.js';

export interface RemoveIslandsOptions {
  /** Alpha above this counts as foreground when labelling. */
  threshold?: number;
  /** Components covering less than this fraction of the frame are deleted. */
  minAreaFraction?: number;
  /**
   * Longest edge of the labelling resolution. Labelling is a fixed-point
   * iteration, so running it small keeps the pass count bounded; specks large
   * enough to matter survive the downscale.
   */
  workingSize?: number;
  /** Propagation dispatches. Defaults to a value derived from `workingSize`. */
  passes?: number;
}

/**
 * Deletes small disconnected components from a matte.
 *
 * This is the "xoa phan thua" step: crops from a marketplace listing routinely
 * bring along a sliver of collar, a watermark corner or JPEG speckle that the
 * network happily keeps. Thresholding alone cannot distinguish them from real
 * artwork - connectivity can.
 *
 * The labelling is an iterative max-propagation (see `islands_propagate.wgsl`)
 * with a bounded pass count, so on pathologically winding shapes it can
 * under-converge and split one component in two. Running at reduced resolution
 * with a generous budget keeps that off the table for realistic artwork, and the
 * conservative default `minAreaFraction` means a mistake removes a speck rather
 * than a design element.
 */
export function removeIslands(
  ctx: GpuContext,
  alpha: GpuImage,
  dst: GpuImage,
  options: RemoveIslandsOptions = {},
): void {
  const threshold = options.threshold ?? 0.5;
  const minAreaFraction = options.minAreaFraction ?? 0.0004;
  const workingSize = Math.max(32, Math.round(options.workingSize ?? 192));

  const scale = Math.min(1, workingSize / Math.max(alpha.width, alpha.height));
  const labelWidth = Math.max(16, Math.round(alpha.width * scale));
  const labelHeight = Math.max(16, Math.round(alpha.height * scale));

  // Dilate before downscaling. An area filter turns a feature thinner than one
  // label-resolution pixel into a sub-threshold value, so it never gets a label
  // - and the apply pass leaves unlabelled pixels alone, meaning a small speck
  // could survive precisely because it was small. Growing by half a label pixel
  // first guarantees every foreground pixel is represented.
  const small = scale >= 1 ? alpha : downscaleForLabels(ctx, alpha, labelWidth, labelHeight, scale);

  const labelsA = ctx.image(small.width, small.height, 'r32uint', 'islands-a');
  const labelsB = ctx.image(small.width, small.height, 'r32uint', 'islands-b');

  ctx.dispatch(
    {
      module: 'islands_init.wgsl',
      bindings: [
        { kind: 'texture', format: small.format },
        { kind: 'storageTexture', format: 'r32uint' },
        { kind: 'uniform' },
      ],
    },
    [
      small,
      labelsA,
      ctx.uniform(new Struct(16).i(0, small.width, small.height).f(8, threshold, 0).bytes),
    ],
    [workgroupCount(small.width, 8), workgroupCount(small.height, 8)],
    'islands:init',
  );

  // Each dispatch propagates at least a full 16px tile, so covering the longest
  // edge twice over leaves a comfortable margin.
  const passes =
    options.passes ?? Math.max(4, Math.ceil(Math.max(small.width, small.height) / 16) * 2 + 4);

  let front = labelsA;
  let back = labelsB;
  const propagateParams = ctx.uniform(
    new Struct(16).i(0, small.width, small.height).u(8, 16, 0).bytes,
  );
  for (let pass = 0; pass < passes; pass++) {
    ctx.dispatch(
      {
        module: 'islands_propagate.wgsl',
        bindings: [
          { kind: 'texture', format: 'r32uint' },
          { kind: 'storageTexture', format: 'r32uint' },
          { kind: 'uniform' },
        ],
      },
      [front, back, propagateParams],
      [workgroupCount(small.width, 16), workgroupCount(small.height, 16)],
      `islands:propagate#${pass}`,
    );
    const swap = front;
    front = back;
    back = swap;
  }

  const capacity = small.width * small.height + 1;
  const counts = ctx.storageBuffer(capacity * 4, 0, 'islands-counts');
  ctx.encoder().clearBuffer(counts, 0, capacity * 4);

  ctx.dispatch(
    {
      module: 'islands_count.wgsl',
      bindings: [
        { kind: 'texture', format: 'r32uint' },
        { kind: 'buffer', access: 'read-write' },
        { kind: 'uniform' },
      ],
    },
    [
      front,
      counts,
      ctx.uniform(new Struct(16).i(0, small.width, small.height).u(8, capacity, 0).bytes),
    ],
    [workgroupCount(small.width, 8), workgroupCount(small.height, 8)],
    'islands:count',
  );

  const minArea = Math.max(1, Math.round(minAreaFraction * small.width * small.height));
  ctx.dispatch(
    {
      module: 'islands_apply.wgsl',
      bindings: [
        { kind: 'texture', format: alpha.format },
        { kind: 'texture', format: 'r32uint' },
        { kind: 'buffer', access: 'read-only' },
        { kind: 'storageTexture', format: 'r32float' },
        { kind: 'uniform' },
      ],
    },
    [
      alpha,
      front,
      counts,
      dst,
      ctx.uniform(
        new Struct(32)
          .i(0, alpha.width, alpha.height)
          .i(8, small.width, small.height)
          .f(16, small.width / alpha.width, small.height / alpha.height)
          .u(24, minArea, capacity).bytes,
      ),
    ],
    [workgroupCount(dst.width, 8), workgroupCount(dst.height, 8)],
    'islands:apply',
  );

  // The counts buffer is referenced by queued commands; hand it back only after
  // this batch is submitted.
  ctx.flush();
  ctx.releaseBuffer(counts);
  labelsA.release();
  labelsB.release();
  if (small !== alpha) small.release();
}

function downscaleForLabels(
  ctx: GpuContext,
  alpha: GpuImage,
  width: number,
  height: number,
  scale: number,
): GpuImage {
  const grown = ctx.image(alpha.width, alpha.height, 'r32float', 'islands-grown');
  morphology(ctx, alpha, grown, Math.max(1, Math.ceil(0.5 / scale)), 'dilate');
  const small = resampleTo(ctx, grown, width, height, { filter: FILTER_AREA });
  grown.release();
  return small;
}
